import {
  AssignedActivitySchedule,
  CustomScheduleResponseFields,
  OccurrenceResponse,
  OccurrenceStatus,
} from '@/hooks/api/assigned-activities/types';

import {
  AccessibilityRestriction,
  ActivityItem,
  ActivityLanguage,
  ActivitySetting,
  ActivityStatus,
  AgeGroup,
  Indication,
  QualityBase,
  RecommendedFrequency,
  TextWithLink,
} from '../activities/types';
import { Gender } from '../patients/types';
import { Picture } from '../types';

export const JOURNAL = 'JOURNAL';

export enum ActivationTime {
  EARLY_MORNING = 'EARLY_MORNING',
  MORNING = 'MORNING',
  NOON = 'NOON',
  AFTERNOON = 'AFTERNOON',
  EVENING = 'EVENING',
  LATE_EVENING = 'LATE_EVENING',
  NIGHT = 'NIGHT',
}

export enum NonActivationTimePeriod {
  ALL_DAY = 'ALL_DAY',
  SITUATIONAL = 'SITUATIONAL',
  INTRODUCTION = 'INTRODUCTION',
}

export const JournalTimePeriod = {
  ...ActivationTime,
  ...NonActivationTimePeriod,
};

// eslint-disable-next-line no-redeclare -- intentional const+type merge, see comment above
export type JournalTimePeriod = ActivationTime | NonActivationTimePeriod;

export interface JournalIntroduction {
  id: string;
  title: string;
  description: string;
  version?: number;
  suggestedDuration?: number;
  items: Array<ActivityItem>;
}

export interface JournalPage {
  id: string;
  status: ActivityStatus;
  title: string;
  feature: string;
  version?: number;
  timePeriod: JournalTimePeriod;
  suggestedDuration?: number;
  items: Array<ActivityItem>;
}

export interface Journal {
  id: string;
  title: string;
  description: string;
  picture?: Picture;
  audioContent?: boolean;
  videoContent?: boolean;
  isSituational: boolean;
  isConditional: boolean;
}

export interface DetailedJournal extends Journal {
  indication?: Array<Indication>;
  contraIndication?: string;
  instruction?: string;
  method?: Array<string>;
  recommendedFrequency?: RecommendedFrequency;
  suggestedDuration?: number;
  gender?: Array<Gender>;
  qualityBase: QualityBase;
  accessibilityRestriction?: AccessibilityRestriction;
  setting?: Array<ActivitySetting>;
  references?: Array<TextWithLink>;
  author?: string;
  patientAgeGroup?: Array<AgeGroup>;
  introduction: JournalIntroduction;
  pages: Array<JournalPage>;
  activityGoal?: string;
  modules?: Array<string>;
  practitionerFeedback?: string;
}

export interface AssignedJournal extends CustomScheduleResponseFields {
  id: string;
  journalId: string;
  assignedBy: string;
  assignedTo: string;
  startDate: string;
  endDate?: string;
  schedule: AssignedActivitySchedule;
  language: ActivityLanguage;
  pages: Array<JournalPage>;
  title: string;
  description: string;
  instruction?: string;
  recommendedFrequency?: RecommendedFrequency;
  picture?: Picture;
  status: OccurrenceStatus;
  audioContent?: boolean;
  videoContent?: boolean;
  isSituational: boolean;
  isConditional: boolean;
  assignedFromGroup?: boolean;
}

export interface AssignedJournalEntry {
  id: string;
  becomesActiveAt: string;
  becomesInactiveAt: string;
  suggestedEndTime?: string;
  completedAt?: string;
  status: OccurrenceStatus;
  timePeriod: JournalTimePeriod;
  duration?: number;
  isConditional: boolean;
}

export interface PatientJournalEntryEvaluation {
  assignedJournalId: string;
  journalPageId: string;
  feature: string;
  timePeriod: JournalTimePeriod;
  responses: Array<OccurrenceResponse>;
}

export interface PatientJournalEntry extends AssignedJournalEntry {
  journalIds: Array<string>;
  assignedJournalIds: Array<string>;
  evaluation?: Array<PatientJournalEntryEvaluation>;
}

export interface FutureJournalEntry {
  id: string;
  becomesActiveAt: string;
  expectedActiveAt?: string;
  timePeriod: JournalTimePeriod;
}

export interface AssignedJournalEntryWithResponses
  extends AssignedJournalEntry {
  items: Array<ActivityItem>;
  responses: Array<OccurrenceResponse>;
  assignedTo: string;
  title: string;
  description?: string;
}

export interface AssignedJournalEvaluationEntry {
  id: string;
  becomesActiveAt: string;
  timePeriod: JournalTimePeriod;
  journalPageId: string;
  feature: string;
  responses: Array<OccurrenceResponse>;
}
